import { useMemo, useState } from 'react'
import { GuildCopperSquareIcon } from '@/atoms/Icons/GuildCopperSquareIcon'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { Image } from '@/atoms/Image'
import { CaptionMD, CaptionSM, Text, TitleSM } from '@/atoms/Text'
import { GuildRecommendedAge } from '@/molecules/GuildRecommendedAge'
import { getInTheatersText } from '@/services/Theatrical/utils'
import { useTranslate } from '@/utils/translate/translate-client'
import { useBasicHeroData } from '../../hooks/useBasicHeroData'
import { useTheatricalMetadata } from '../../hooks/useTheatricalMetadata'
import { ParentsGuide } from './ShowtimeDetailsPanel/ParentsGuide/ParentsGuide'
import { useTicketsContext } from './TicketsContext'
import { TrailerModal } from './modals/TrailerModal'

interface Props {
  theatricalSlug: string
}
export const BasicHero = ({ theatricalSlug }: Props) => {
  const { t } = useTranslate('tickets')
  const { theatricalRelease, projectGuildScore, experiments } = useTicketsContext()
  const [isTrailerModalOpen, setIsTrailerModalOpen] = useState(false)

  const basicHeroData = useBasicHeroData(theatricalSlug)

  const trailerUrl = experiments?.['solo-mio-showtimes-trailer']
    ? 'https://www.youtube-nocookie.com/embed/Vy45xJxnCZM'
    : theatricalRelease?.region?.trailerUrl

  const metadataItems = useTheatricalMetadata({
    rating: theatricalRelease?.movieRating,
    movieDuration: theatricalRelease?.movieDuration,
    projectGuildScore,
    critics: theatricalRelease?.critics,
    guildScoreOptions: {
      icon: <GuildCopperSquareIcon size={16} />,
      triggerTextClassName: '!photon-caption-sm md:!photon-caption-md',
    },
    iconOptions: {
      guildIconColor: 'white',
    },
  })

  const inTheatersText = useMemo(() => {
    const releaseDate = theatricalRelease?.region?.releaseDate
    if (!releaseDate) return null

    return getInTheatersText(releaseDate, t)
  }, [t, theatricalRelease?.region?.releaseDate])

  if (!basicHeroData) return null

  const { bgVideoUrl, fallbackImageUrl, logo } = basicHeroData

  return (
    <div className="relative z-[9999] flex min-h-[240px] w-full shrink-0 flex-col bg-black p-4 pt-16 @container md:h-[320px] md:px-10 md:py-8 lg:h-[480px] lg:px-12">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <Image alt="" className="object-cover" src={fallbackImageUrl} fill sizes="100vw" priority />

        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute size-full max-w-none object-cover"
          style={{ objectPosition: '50% 20%' }}
        >
          <source src={bgVideoUrl} type="video/mp4" />
        </video>
      </div>

      <div className="absolute inset-0 bg-black/50" />
      <div
        className="absolute inset-0 md:hidden"
        style={{ background: 'linear-gradient(250deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.30) 100%)' }}
      />

      <div className="z-10 mx-auto mt-auto flex w-full max-w-[1440px] justify-between @[1440px]:px-12">
        <div>
          <Image
            alt="logo"
            className={logo.class}
            src={logo.url}
            width={logo.width}
            height={logo.height}
            sizes="(max-width: 1023px) 180px, 265px"
            priority
          />

          <div className="mt-4 flex flex-col gap-1.5 md:gap-2">
            {inTheatersText && (
              <TitleSM color="white" weight="semibold">
                {inTheatersText}
              </TitleSM>
            )}

            <div className="flex items-center gap-2">
              <GuildRecommendedAge
                compact
                projectSlug={theatricalSlug}
                overrideButtonClassName="!bg-core-gray-200 !text-core-gray-800"
              />
              <ParentsGuide
                popoverClassName="!mt-0"
                theatricalSlug={theatricalSlug}
                triggerOptions={{
                  iconColor: 'warm-03',
                  textColor: 'warm-03',
                  iconClassName: '!size-[12px]',
                  textClassName: '!text-[.6875rem]',
                }}
              />
            </div>

            {metadataItems.length > 0 && (
              <>
                {(() => {
                  const shouldSeparateOnMobile = metadataItems.length > 4
                  const topItems = metadataItems.filter((item) => item.type === 'rating' || item.type === 'duration')
                  const bottomItems = metadataItems.filter((item) => item.type !== 'rating' && item.type !== 'duration')

                  if (!shouldSeparateOnMobile) {
                    return (
                      <div className="flex items-center gap-1.5">
                        {metadataItems.map((item, index) => (
                          <MetadataItem key={item.id} item={item} index={index} />
                        ))}
                      </div>
                    )
                  }

                  return (
                    <div className="md:flex md:items-center md:gap-1.5">
                      {topItems.length > 0 && (
                        <div className="flex items-center gap-1.5">
                          {topItems.map((item, index) => (
                            <MetadataItem
                              key={item.id}
                              item={item}
                              index={index}
                              separatorClassName="hidden opacity-50 md:block"
                              containerClassName="flex items-center space-x-1.5"
                            />
                          ))}
                        </div>
                      )}

                      {bottomItems.length > 0 && (
                        <div className="mt-2 flex items-center gap-1.5 md:mt-0">
                          <CaptionMD color="white" className="hidden opacity-50 md:block">
                            •
                          </CaptionMD>
                          {bottomItems.map((item, index) => (
                            <MetadataItem key={item.id} item={item} index={index} />
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })()}
              </>
            )}
          </div>
        </div>

        {trailerUrl && (
          <button
            onClick={() => setIsTrailerModalOpen(true)}
            className="mt-auto flex shrink-0 flex-col items-center justify-center gap-1.5 lg:absolute lg:left-1/2 lg:top-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2"
          >
            <span className="aspect-square flex shrink-0 items-center justify-center rounded-full bg-white p-3 transition-all duration-150 ease-in-out hover:bg-white/40 lg:p-4">
              <PlayIcon size={24} className="lg:size-8" color="core-gray-950" />
            </span>

            <CaptionSM
              className="!text-xs lg:!text-sm"
              color="white"
              style={{ textShadow: '1px 1px 0px rgba(0, 0, 0, 0.35)', color: 'white' }}
            >
              {t('playTrailerHero', 'play trailer')}
            </CaptionSM>
          </button>
        )}
      </div>

      {trailerUrl && (
        <TrailerModal open={isTrailerModalOpen} onClose={() => setIsTrailerModalOpen(false)} trailerUrl={trailerUrl} />
      )}
    </div>
  )
}

interface MetadataItemProps {
  item: {
    id: string
    icon?: React.ReactNode
    content: React.ReactNode
  }
  index: number
  separatorClassName?: string
  containerClassName?: string
}

const MetadataItem = ({
  item,
  index,
  separatorClassName = 'opacity-50',
  containerClassName = 'flex items-center gap-1.5',
}: MetadataItemProps) => {
  return (
    <div className={containerClassName}>
      {index > 0 && (
        <CaptionMD color="white" className={separatorClassName}>
          •
        </CaptionMD>
      )}
      <div className="flex items-center gap-1">
        {item.icon}
        <Text className="photon-caption-sm md:photon-caption-md" color="white">
          {item.content}
        </Text>
      </div>
    </div>
  )
}
